// Shared constants, types and pure helpers for the Team Management V2 shell.
// V2 is a presentational reskin of the existing Team page behind the
// `team_management_v2` per-tenant flag; it reuses the exact data sources and
// callables the legacy page uses. No new backend, roles or models.

import type { CSSProperties } from 'react';

export type TeamMemberStatus = 'active' | 'revoked' | string;

// Shape of a member as provided by the AppLayout outlet context
// (`teamMembers`, sourced from `useCompanyData`). Mirrors the fields the legacy
// TeamManagementPage reads off each member.
//
// The broker-filtered-admin predicate that used to live here was PR-F's
// standalone copy, carried so PR-F could build and ship before PR-F2 landed.
// Both are now in one tree, so the copy is gone and the two pages read
// `isBrokerFilteredAdmin` off `useAuth()`, which AuthContext computes with the
// single `isBrokerFilteredAdminClaims` from the same `claims` object.

export interface TeamMember {
  id: string;
  displayName?: string;
  email?: string;
  role: string;
  status: TeamMemberStatus;
  assignedOffices?: string[];
  canSeeAllOffices?: boolean;
  // Optimistic-concurrency counter owned by updateUserOfficeScope. Absent = 0.
  officeScopeRevision?: number;
  isFounder?: boolean;
  brokerDocId?: string;
  hideCommissions?: boolean;
  hideLoanAmounts?: boolean;
  // Per-user portfolio-VOLUME switch for a hidden user (loan-book totals and
  // lender percentages survive the strip). Absent = today's behaviour.
  showLoanTotals?: boolean;
  allowedReports?: string[];
  allowedLoanBookIds?: string[];
  allowedTemplateIds?: string[];
  linkedBrokerDocIds?: string[];
  // Per-(broker, loan book) view grants. Present on broker-family user docs.
  linkedLoanBookGrants?: Array<{ brokerDocId: string; loanBookId: string }>;
  // Lead-capture nav visibility filter (broker-family targets).
  allowedLeadPages?: string[];
  // Administrator page / report deny-lists (config/adminAccess.ts).
  deniedAdminPages?: string[];
  deniedAdminReports?: string[];
  createdAt?: unknown;
}

export interface OfficeLike {
  id: string;
  name?: string;
}

// Shared confirm-dialog request, raised by tabs and serviced by the single
// ConfirmDialog the composition root owns. Keeps one dialog for the whole page.
export interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'danger' | 'primary';
  onConfirm: () => void;
}

// Human-readable role labels. Keys match the RBAC role claim values.
export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  administrator: 'Administrator',
  office_admin: 'Office Admin',
  credit_analyst: 'Credit Analyst',
  broker: 'Broker',
  marketing: 'Marketing',
  broker_assistant: 'Broker Assistant',
};

export function roleLabel(role: string): string {
  return (
    ROLE_LABELS[role] ||
    role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Roles that always see commission values and full scope (administrator tier).
export const ADMIN_ROLES = new Set(['super_admin', 'administrator']);

// Card groupings, matching the approved mockup: Leadership / Brokers /
// Support & Operations.
export interface RoleGroup {
  id: 'leadership' | 'brokers' | 'support';
  label: string;
  roles: string[];
  // Tailwind text colour for the section heading accent.
  accentClass: string;
}

export const ROLE_GROUPS: RoleGroup[] = [
  {
    id: 'leadership',
    label: 'Leadership',
    roles: ['super_admin', 'administrator', 'office_admin'],
    accentClass: 'text-violet-300',
  },
  {
    id: 'brokers',
    label: 'Brokers',
    roles: ['broker'],
    accentClass: 'text-blue-400',
  },
  {
    id: 'support',
    label: 'Support & Operations',
    roles: ['credit_analyst', 'broker_assistant', 'marketing'],
    accentClass: 'text-teal-300',
  },
];

// Avatar gradient palette (from / to). Chosen deterministically per name so a
// person keeps the same avatar colour across renders.
const AVATAR_PALETTE: Array<[string, string]> = [
  ['#2563eb', '#1d4ed8'],
  ['#6366f1', '#4f46e5'],
  ['#8b5cf6', '#7c3aed'],
  ['#0891b2', '#0e7490'],
  ['#059669', '#047857'],
  ['#db2777', '#be185d'],
  ['#d97706', '#b45309'],
  ['#475569', '#334155'],
];

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function nameHash(name: string): number {
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i);
  return sum;
}

export function avatarGradient(name: string): CSSProperties {
  const [from, to] = AVATAR_PALETTE[nameHash(name) % AVATAR_PALETTE.length];
  return { backgroundImage: `linear-gradient(135deg, ${from}, ${to})` };
}

export function memberDisplayName(m: TeamMember): string {
  return m.displayName || m.email || 'Unknown user';
}

export function officeText(m: TeamMember, officeNameById: Map<string, string>): string {
  if (m.canSeeAllOffices || m.role === 'super_admin') return 'All offices';
  const names = (m.assignedOffices ?? [])
    .map((id) => officeNameById.get(id))
    .filter((name): name is string => Boolean(name));
  if (names.length > 0) return names.join(' · ');
  return (m.assignedOffices ?? []).length > 0 ? 'Offices not found' : 'No offices assigned';
}

// Access-coverage tone for the per-card bar. Cosmetic only; derived from the
// same access fields the legacy page persists.
export type CoverageTone = 'complete' | 'office' | 'custom' | 'attention';

export interface CoverageResult {
  label: string;
  percent: number;
  tone: CoverageTone;
}

// `undefined` allowlist means unrestricted ("All") for that dimension.
export function reportsDisplay(m: TeamMember): string | number {
  return m.allowedReports === undefined ? 'All' : m.allowedReports.length;
}

export function loanBooksDisplay(m: TeamMember): string | number {
  const list = m.allowedLoanBookIds ?? m.allowedTemplateIds;
  return list === undefined ? 'All' : list.length;
}

// Strict per-tenant flag decision. Deliberately a direct boolean equality on
// the company doc's featureFlags map - no tier fallback, no additive override,
// no truthiness coercion (so 'true', 1 or 'enabled' all read as OFF).
export function isTeamManagementV2Enabled(
  companyData: { featureFlags?: Record<string, unknown> } | null | undefined,
): boolean {
  return companyData?.featureFlags?.team_management_v2 === true;
}

export interface MemberSearchFilters {
  query: string;
  role: string;
  officeId: string;
}

// Pure People-tab filter: name/email search, role, and office. Mirrors the
// legacy page's matching semantics (super_admin / canSeeAllOffices count as
// present in every office).
export function filterMembers(
  members: TeamMember[],
  filters: MemberSearchFilters,
): TeamMember[] {
  const needle = filters.query.trim().toLowerCase();
  return members.filter((m) => {
    if (needle) {
      const name = memberDisplayName(m).toLowerCase();
      const email = (m.email ?? '').toLowerCase();
      if (!name.includes(needle) && !email.includes(needle)) return false;
    }
    if (filters.role && m.role !== filters.role) return false;
    if (filters.officeId) {
      const inOffice =
        !!m.canSeeAllOffices ||
        m.role === 'super_admin' ||
        (m.assignedOffices ?? []).includes(filters.officeId);
      if (!inOffice) return false;
    }
    return true;
  });
}

export interface MemberGroup {
  group: RoleGroup;
  members: TeamMember[];
}

// Buckets members into the display groups, dropping any empty group.
export function groupMembers(members: TeamMember[]): MemberGroup[] {
  return ROLE_GROUPS.map((group) => ({
    group,
    members: members.filter((m) => group.roles.includes(m.role)),
  })).filter((g) => g.members.length > 0);
}

export function computeCoverage(m: TeamMember): CoverageResult {
  const unrestrictedReports = m.allowedReports === undefined;
  const unrestrictedBooks =
    (m.allowedLoanBookIds ?? m.allowedTemplateIds) === undefined;

  if (ADMIN_ROLES.has(m.role) || m.canSeeAllOffices) {
    if (unrestrictedReports && unrestrictedBooks) {
      return { label: 'Complete', percent: 100, tone: 'complete' };
    }
  }

  const bookLen = (m.allowedLoanBookIds ?? m.allowedTemplateIds ?? []).length;
  const reportLen = (m.allowedReports ?? []).length;

  if (m.role === 'broker' && bookLen === 0 && reportLen === 0) {
    return { label: 'Needs attention', percent: 12, tone: 'attention' };
  }

  if (m.role === 'office_admin') {
    return { label: 'Office-wide', percent: 82, tone: 'office' };
  }

  // Rough proportional fill for custom grants; bounded so the bar stays visible.
  const denom = 12; // nominal report catalogue size for display purposes only
  const est = Math.round(((reportLen + bookLen) / (denom * 2)) * 100);
  return { label: 'Custom', percent: Math.min(100, Math.max(10, est)), tone: 'custom' };
}
